import React from 'react';
import { ArrowLeftOutlined, CalendarOutlined, DollarOutlined, EnvironmentOutlined, GlobalOutlined, TeamOutlined } from '@ant-design/icons';
import { PrimaryButton } from '@components/atoms';
import { useProjects } from '@hooks';
import { Buildings } from '@phosphor-icons/react';
import { Alert, Button, Col, Divider, Row, Skeleton, Space, Tag, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

export const ProjectDetailsPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { getProject } = useProjects();

  // Get project details - always call the hook
  const projectQuery = getProject(projectId || '');

  // Handle back navigation
  const handleBack = () => {
    navigate('/projects');
  };

  // Handle contact button click
  const handleContact = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); 
    // Navigate to contact page or contact section
    navigate('/contact');                   
    // Small timeout to ensure navigation completes before scrolling
    setTimeout(() => {
      // Look for contact form element
      const contactForm = document.getElementById('contact-form');
      if (contactForm) {
        // Calculate position to show section title (50px above element)
        const offset = contactForm.getBoundingClientRect().top + window.pageYOffset - 50;
        // Scroll to calculated position
        window.scrollTo({
          top: offset,
          behavior: 'smooth'
        });
      }
    }, 300);
  };

  // Helper functions to handle both new and legacy field names
  const getProjectDisplayName = (project: any) => {
    return project.name || project.title || 'Untitled Project';
  };

  const getProjectImage = (project: any) => {
    const imageUrl = import.meta.env.VITE_URL_FILES + '/' + project.image;
    return imageUrl || '/project-default.jpg';
  };

  const getProjectDescription = (project: any) => {
    return project.description || 'No description available';
  };

  const getProjectYear = (project: any) => {
    if (project.completionYear) {
      return project.completionYear.toString();
    }
    
    if (project.endDate) {
      try {
        return new Date(project.endDate).getFullYear().toString();
      } catch {
        // fallback
      }
    }
    
    return project.year || 'N/A';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  // Loading state
  if (projectQuery?.isLoading) {
    return (
      <div className="bg-black min-h-screen text-white py-10">
        <div className="container mx-auto px-4">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="mb-6 !text-white hover:!text-primary"
          >
            Back to projects
          </Button>
          <Skeleton active paragraph={{ rows: 12 }} />
        </div>
      </div>
    );
  }

  // Error state
  if (projectQuery?.isError || !projectQuery?.data) {
    return (
      <div className="bg-black min-h-screen text-white py-10">
        <div className="container mx-auto px-4">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="mb-6 !text-white hover:!text-primary"
          >
            Back to projects
          </Button>
          <Alert
            message="Project Not Found"
            description="The project you're looking for could not be found. Please try another project or check back later."
            type="error"
            showIcon
            className="mb-8"
            action={
              <Button onClick={handleBack}>View all projects</Button>
            }
          />
        </div>
      </div>
    );
  }

  const project = projectQuery.data;

  return (
    <div className="bg-red-500 min-h-screen text-white">
      {/* Hero Section */}
      <section className="w-full py-16 bg-zinc-900 relative">
        <div className="absolute inset-0 w-full h-full flex items-center justify-center">
          <img
            src={getProjectImage(project)}
            alt={getProjectDisplayName(project)}
            className="w-full h-full object-cover opacity-40"
            onError={(e) => {
              e.currentTarget.src = project.placeholderImage || 'https://placehold.co/1200x600?text=Project+Image';
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="mb-6 !text-white hover:!text-primary"
          >
            Back to projects
          </Button>
          <Title level={1} className="!text-white !font-['Android_101'] !font-normal uppercase mb-4">
            {getProjectDisplayName(project)}
          </Title>
          <div className="flex flex-wrap gap-4 mb-6 items-center">
            <div className="flex items-center">
              <CalendarOutlined className="mr-2 text-gray-400" />
              <Text className="!text-white !font-['Barlow']">
                {project.duration || 'Duration not specified'}
              </Text>
            </div>
            {project.budget && (
              <div className="flex items-center">
                <DollarOutlined className="mr-2 text-[#E3AF25]" />
                <Text className="!text-[#E3AF25] !font-['Barlow']">{project.budget}</Text>
              </div>
            )}
            <div className="flex items-center">
              <EnvironmentOutlined className="mr-2 text-gray-400" />
              <Text className="!text-gray-400 !font-['Barlow']">
                Completed in {getProjectYear(project)}
              </Text>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            {project.siteArea && (
              <Tag className="!py-1 !px-4 !text-white !bg-transparent !border-white">
                Site: {project.siteArea}
              </Tag>
            )}
            {project.earthMoved && (
              <Tag className="!py-1 !px-4 !text-white !bg-transparent !border-white">
                Earth: {project.earthMoved}
              </Tag>
            )}
            {(project.isHighlight || project.featured) && (
              <Tag className="!py-1 !px-4 !text-black !bg-[#E3AF25] !border-none">Highlighted</Tag>
            )}
          </div>
        </div>
      </section>

      {/* Project Details Section */}
      <section className="w-full py-16 bg-second-black-carbon">
        <div className="container mx-auto px-4">
          <Row gutter={[48, 32]}>
            <Col xs={24} lg={16}>
              <div className="bg-zinc-900 p-8 mb-8">
                <Title level={3} className="!text-white !font-['Android_101'] !font-normal mb-6">PROJECT OVERVIEW</Title>
                <Paragraph className="!text-gray-300 !font-['Barlow'] whitespace-pre-line mb-6 text-lg">
                  {getProjectDescription(project)}
                </Paragraph>

                <Divider className="!bg-zinc-700 my-8" />

                <Title level={3} className="!text-white !font-['Android_101'] !font-normal mb-6">PROJECT SPECIFICATIONS</Title>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {project.startDate && (
                    <div>
                      <Text className="!text-gray-400 !font-['Barlow'] block mb-1">Start Date</Text>
                      <Text className="!text-white !font-['Barlow'] text-lg">{formatDate(project.startDate)}</Text>
                    </div>
                  )}
                  {project.endDate && (
                    <div>
                      <Text className="!text-gray-400 !font-['Barlow'] block mb-1">End Date</Text>
                      <Text className="!text-white !font-['Barlow'] text-lg">{formatDate(project.endDate)}</Text>
                    </div>
                  )}
                  {project.duration && (
                    <div>
                      <Text className="!text-gray-400 !font-['Barlow'] block mb-1">Duration</Text>
                      <Text className="!text-white !font-['Barlow'] text-lg">{project.duration}</Text>
                    </div>
                  )}
                  {project.siteArea && (
                    <div>
                      <Text className="!text-gray-400 !font-['Barlow'] block mb-1">Site Area</Text>
                      <Text className="!text-white !font-['Barlow'] text-lg">{project.siteArea}</Text>
                    </div>
                  )}
                  {project.earthMoved && (
                    <div>
                      <Text className="!text-gray-400 !font-['Barlow'] block mb-1">Earth Moved</Text>
                      <Text className="!text-white !font-['Barlow'] text-lg">{project.earthMoved}</Text>
                    </div>
                  )}
                  {project.budget && (
                    <div>
                      <Text className="!text-gray-400 !font-['Barlow'] block mb-1">Budget</Text>
                      <Text className="!text-white !font-['Barlow'] text-lg">{project.budget}</Text>
                    </div>
                  )}
                </div>

                <Divider className="!bg-zinc-700 my-8" />

                <Title level={3} className="!text-white !font-['Android_101'] !font-normal mb-6">PROJECT GALLERY</Title>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="aspect-video">
                    <img 
                      src={getProjectImage(project)}
                      alt={`${getProjectDisplayName(project)} - Main view`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = project.placeholderImage || 'https://placehold.co/800x500?text=Project+Image';
                      }}
                    />
                  </div>
                  {/* Additional images can be added here when available */}
                </div>
              </div>
            </Col>

            <Col xs={24} lg={8}>
              <div className="bg-zinc-900 p-8 mb-8">
                <Title level={4} className="!text-white !font-['Android_101'] !font-normal mb-4">COMPANY INFO</Title>
                <Space direction="vertical" size="large" className="w-full">
                  <div className="flex items-center">
                                    <img src="/assets/logos/ruppert-logo.svg" alt="Estabrook Inc. Logo" className="w-12 h-12" />
                <div className="ml-4">
                  <Text className="!text-white !font-['Barlow'] block">Estabrook Inc.</Text>
                      <Text className="!text-gray-400 !font-['Barlow'] text-sm">Construction & Excavation</Text>
                    </div>
                  </div>
                  <Divider className="!bg-zinc-700 my-4" />
                  <div>
                    <div className="flex items-center mb-4">
                      <EnvironmentOutlined className="text-gray-400 mr-3 text-xl" />
                      <Text className="!text-gray-300 !font-['Barlow']">Truckee, California</Text>
                    </div>
                    <div className="flex items-center mb-4">
                      <TeamOutlined className="text-gray-400 mr-3 text-xl" />
                      <Text className="!text-gray-300 !font-['Barlow']">50-100 employees</Text>
                    </div>
                    <div className="flex items-center mb-4">
                      <Buildings className="text-gray-400 mr-3" weight="fill" size={20} />
                      <Text className="!text-gray-300 !font-['Barlow']">Construction & Excavation</Text>
                    </div>
                    <div className="flex items-center">
                      <GlobalOutlined className="text-gray-400 mr-3" style={{ fontSize: '20px' }} />
                      <Text className="!text-gray-300 !font-['Barlow']">
                                              <a href="https://www.estabrookinc.com" target="_blank" rel="noopener noreferrer" className="!text-gray-300 !font-['Barlow'] hover:!text-[#E3AF25] hover:underline">
                        www.estabrookinc.com
                        </a>
                      </Text>
                    </div>
                  </div>
                </Space>
              </div>

              <div className="bg-zinc-900 p-8">
                <Title level={4} className="!text-white !font-['Android_101'] !font-normal mb-4">INTERESTED IN SIMILAR WORK?</Title>
                <Paragraph className="!text-gray-300 !font-['Barlow'] mb-4 text-sm">
                  Contact us to discuss your next construction or excavation project.
                </Paragraph>
                <div className="flex justify-center">
                  <PrimaryButton
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleContact(e)}
                    className="!px-8 !py-3 !h-auto !font-['Android_101'] !text-base w-full"
                  >
                    CONTACT US
                  </PrimaryButton>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Related Projects Section */}
      <section className="w-full py-16 bg-zinc-900">
        <div className="container mx-auto px-4">
          <Title level={2} className="!text-white !font-['Android_101'] !font-normal mb-8 text-center">View all projects</Title>
          <Paragraph className="!text-gray-300 !font-['Barlow'] text-center mb-10">
            Explore other projects that showcase our expertise
          </Paragraph>

          <div className="text-center">
            <Button
              onClick={handleBack}
              className="border-white text-white hover:!bg-primary hover:!text-black hover:!border-primary px-8 py-3 h-auto"
            >
              VIEW ALL PROJECTS
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}; 